import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  DOCUMENT,
  inject
} from '@angular/core';

@Directive({
  selector: '[nowWbsucheLibFocusableIfVisible]'
})
export class FocusableIfVisibleDirective implements OnDestroy, OnInit, AfterViewInit, OnChanges {
  private readonly element = inject(ElementRef);
  private readonly document = inject<Document>(DOCUMENT);

  @Input() nowWbsucheLibFocusableIfVisible?: boolean = true;

  private observer: IntersectionObserver | undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.nowWbsucheLibFocusableIfVisible.currentValue !=
      changes.nowWbsucheLibFocusableIfVisible.previousValue
    ) {
      if (changes.nowWbsucheLibFocusableIfVisible.currentValue === false) {
        this.unobserve();
        this.element.nativeElement.removeAttribute('tabindex');
      } else {
        this.createObserver();
        this.startObservingElement();
      }
    }
  }

  ngOnInit() {
    this.createObserver();
  }

  ngAfterViewInit() {
    this.startObservingElement();
  }

  ngOnDestroy() {
    this.unobserve();
  }

  private unobserve(): void {
    if (this.observer) {
      this.observer.unobserve(this.element.nativeElement);
      this.observer.disconnect();
      this.observer = undefined;
    }
  }

  private createObserver() {
    const isIntersecting = (entry: IntersectionObserverEntry) =>
      entry.isIntersecting || entry.intersectionRatio > 0;

    // Oberver pattern helps focusing in Firefox
    // The observer "triggers" as soon as the intersecting element
    // is in the viewport of the browser
    this.observer = new this.document.defaultView!.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (this.nowWbsucheLibFocusableIfVisible === true) {
          if (isIntersecting(entry)) {
            this.makeElementFocusable();
          } else {
            this.makeElementNotFocusable();
          }
        }
      });
    });
  }

  private startObservingElement() {
    if (!this.observer) {
      return;
    }

    this.observer.observe(this.element.nativeElement);
  }

  private makeElementFocusable(): void {
    this.element.nativeElement.setAttribute('tabindex', '0');
  }

  private makeElementNotFocusable(): void {
    this.element.nativeElement.setAttribute('tabindex', '-1');
  }
}
